import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '@/hooks/useAuth'
import { useRecentOrders, useCustomerProfile } from '@/hooks/useOrders'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Package, ArrowRight, ShoppingBag, Settings, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import type { FulfillmentStatus } from '@/types/database'
import { getPageTitle } from '@/lib/branding'

const statusColors: Record<FulfillmentStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
}

export function AccountDashboard() {
  const { user } = useAuth()
  const { profile, isLoading: profileLoading } = useCustomerProfile()
  const { orders, totalCount, isLoading: ordersLoading } = useRecentOrders(3)

  const isLoading = profileLoading || ordersLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'there'

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Account Dashboard')}</title>
      </Helmet>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Welcome Header */}
        <div>
          <h1 className="text-2xl font-bold">Welcome back, {displayName}</h1>
          <p className="text-muted-foreground mt-1">
            Manage your orders, profile, and account settings
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
              <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalCount}</div>
              <p className="text-xs text-muted-foreground">
                All time purchases
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Profile</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium truncate">{user?.email}</div>
              <Link to="/account/profile">
                <Button variant="link" className="h-auto p-0 text-xs">
                  Edit profile
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Quick Links</CardTitle>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="space-y-1">
              <Link to="/" className="block text-sm text-muted-foreground hover:text-foreground">
                Browse products
              </Link>
              <Link to="/past-reviews" className="block text-sm text-muted-foreground hover:text-foreground">
                View past reviews
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Recent Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Orders</CardTitle>
              <CardDescription>Your most recent purchases</CardDescription>
            </div>
            <Link to="/account/orders">
              <Button variant="outline" size="sm">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/account/orders/${order.id}`}
                    className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    {/* Thumbnail */}
                    <div className="h-16 w-16 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {order.product?.video_thumbnail_url ? (
                        <img
                          src={order.product.video_thumbnail_url}
                          alt={order.product.title || 'Product'}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Package className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>

                    {/* Order Details */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {order.product?.title || 'Product'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(order.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>

                    {/* Status & Price */}
                    <div className="text-right flex-shrink-0">
                      <Badge className={statusColors[order.fulfillment_status]}>
                        {order.fulfillment_status}
                      </Badge>
                      <p className="text-sm font-medium mt-1">
                        ${Number(order.total_amount).toFixed(2)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No orders yet</h3>
                <p className="text-muted-foreground mt-1">
                  When you make a purchase, it will appear here
                </p>
                <Link to="/">
                  <Button className="mt-4">Browse Products</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
